import { useWorkspaceText } from '../../interface-copy';
import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { rowsLabel } from '../table/copy';
import { formatNumber } from '../money.ts';
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from '@/components/ui/menu';
import { AXIS_FONT, isHorizontal, labelStride, valueDomain } from './native-layout';
import { nativeValueFormat } from './native-format';
import type { ChartPayload, TablePayload } from '../../types';
import { splitDomMetadata, type DomMetadata } from '../../dom-metadata';

export function NativeWorkspaceChart({
  chart,
  columns,
  locale,
  ...props
}: {
  chart: ChartPayload;
  columns?: TablePayload['columns'];
  locale?: string;
} & DomMetadata) {
  const { text: uiText, rich: uiRich, locale: interfaceLocale } = useWorkspaceText();
  const { metadata } = splitDomMetadata(props);
  const [selected, setSelected] = useState(chart.yKeys[0]);
  const box = useRef<HTMLDivElement>(null);
  // Drawn at real pixel size so axis text is 12 px on every screen, never scaled down.
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setWidth(Math.max(240, element.clientWidth)));
    observer.observe(element);
    setWidth(Math.max(240, element.clientWidth));
    return () => observer.disconnect();
  }, []);
  const key = chart.yKeys.includes(selected) ? selected : chart.yKeys[0];
  const label = (columnKey: string) =>
    columns?.find((column) => column.key === columnKey)?.label ?? columnKey;
  const rows = chart.rows.slice(0, 24);
  const values = rows.map((row) =>
    typeof row[key] === 'number' && Number.isFinite(row[key]) ? (row[key] as number) : null,
  );
  const valid = values.filter((value): value is number => value !== null);
  if (!key || !valid.length)
    return (
      <p className="ws-muted" {...metadata}>
        {uiText(
          'There are no numeric values available to chart. View the table for the original data.',
        )}
      </p>
    );
  const labels = rows.map((row) => String(row[chart.xKey] ?? ''));
  const horizontal = isHorizontal(chart.type, labels);
  const scale = valueDomain(chart.type, valid);
  const { tick: format, value: formatValue } = nativeValueFormat(chart, columns, key, {
    locale,
    maximumTick: Math.max(...scale.ticks.map(Math.abs)),
  });
  const tickWidth = Math.max(...scale.ticks.map((tick) => format(tick).length)) * 7 + 12;
  const labelWidth = horizontal
    ? Math.min(160, Math.max(...labels.map((l) => l.length)) * 7 + 12)
    : 0;
  const left = horizontal ? labelWidth : tickWidth;
  const right = 16;
  const top = 12;
  const bottomPad = horizontal ? 24 : 28;
  const height = horizontal ? top + rows.length * 32 + bottomPad : 280;
  const plotW = Math.max(40, width - left - right);
  const plotH = height - top - bottomPad;
  const span = scale.top - scale.bottom || 1;
  const along = (value: number) => ((value - scale.bottom) / span) * (horizontal ? plotW : plotH);
  // Thickness: at most 40 px, centred in the slot (a single series is not a slab).
  const slot = (horizontal ? plotH : plotW) / Math.max(rows.length, 1);
  const thick = Math.min(40, Math.max(3, slot * 0.6));
  const at = (index: number) => (horizontal ? top : left) + index * slot + (slot - thick) / 2;
  const valueY = (value: number) => top + plotH - along(value);
  const valueX = (value: number) => left + along(value);
  const stride = horizontal ? 1 : labelStride(labels, slot);
  const points = values
    .map((value, index) => (value === null ? null : `${at(index) + thick / 2},${valueY(value)}`))
    .reduce<string[][]>(
      (runs, point) => {
        if (point === null) runs.push([]);
        else runs[runs.length - 1].push(point);
        return runs;
      },
      [[]],
    );
  return (
    <div className="ws-chart" ref={box} {...metadata}>
      <div className="ws-chart-tools">
        <Menu>
          <MenuTrigger className="ws-chart-value" aria-label={uiText('Chart value')}>
            {label(key)}
            <ChevronDownIcon size={14} aria-hidden />
          </MenuTrigger>
          <MenuContent label={uiText('Chart value')} align="start">
            <MenuRadioGroup value={key} onValueChange={setSelected}>
              {chart.yKeys.map((name) => (
                <MenuRadioItem key={name} value={name}>
                  {label(name)}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuContent>
        </Menu>
        <span>
          {uiRich('{value1} of {value2} shown', {
            value1: formatNumber(rows.length, interfaceLocale),
            value2: rowsLabel(chart.rows.length, interfaceLocale),
          })}
        </span>
      </div>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={uiText('{value1} by {value2}; {value3} rows. View Table for exact values.', {
          value1: label(key),
          value2: label(chart.xKey),
          value3: rows.length,
        })}
      >
        {scale.ticks.map((tick) => (
          <g key={tick}>
            {horizontal ? (
              <>
                <line
                  x1={valueX(tick)}
                  x2={valueX(tick)}
                  y1={top}
                  y2={top + plotH}
                  stroke="var(--ws-line)"
                />
                <text
                  x={valueX(tick)}
                  y={height - 6}
                  textAnchor="middle"
                  fill="var(--ws-muted)"
                  fontSize={AXIS_FONT}
                >
                  {format(tick)}
                </text>
              </>
            ) : (
              <>
                <line
                  x1={left}
                  x2={left + plotW}
                  y1={valueY(tick)}
                  y2={valueY(tick)}
                  stroke="var(--ws-line)"
                />
                <text
                  x={left - 8}
                  y={valueY(tick) + 4}
                  textAnchor="end"
                  fill="var(--ws-muted)"
                  fontSize={AXIS_FONT}
                >
                  {format(tick)}
                </text>
              </>
            )}
          </g>
        ))}
        {chart.type === 'line' &&
          points
            .filter((run) => run.length > 1)
            .map((run, index) => (
              <polyline
                key={index}
                points={run.join(' ')}
                fill="none"
                stroke="var(--ws-chart-ink)"
                strokeWidth="2"
              />
            ))}
        {rows.map((row, index) => (
          <g key={index}>
            {values[index] !== null &&
              (chart.type === 'line' ? (
                <circle
                  cx={at(index) + thick / 2}
                  cy={valueY(values[index]!)}
                  r="3"
                  fill="var(--ws-chart-ink)"
                >
                  <title>{`${labels[index]}: ${formatValue(values[index]!)}`}</title>
                </circle>
              ) : (
                <path
                  d={
                    horizontal
                      ? hBarPath(valueX(0), at(index), valueX(values[index]!) - valueX(0), thick)
                      : barPath(at(index), valueY(0), thick, valueY(values[index]!) - valueY(0))
                  }
                  fill="var(--ws-chart-ink)"
                >
                  <title>{`${labels[index]}: ${formatValue(values[index]!)}`}</title>
                </path>
              ))}
            {(horizontal || index % stride === 0) && (
              <text
                x={horizontal ? left - 8 : at(index) + thick / 2}
                y={horizontal ? at(index) + thick / 2 + 4 : height - 8}
                textAnchor={horizontal ? 'end' : 'middle'}
                fill="var(--ws-muted)"
                fontSize={AXIS_FONT}
              >
                {labels[index].length > 24 ? labels[index].slice(0, 23) + '…' : labels[index]}
              </text>
            )}
          </g>
        ))}
      </svg>
      <p className="ws-page-note">
        {uiText('Values come directly from the source data. Missing values are not estimates.')}
      </p>
    </div>
  );
}

/** A horizontal bar with a 4 px rounded data end and a square baseline. */
function hBarPath(baseline: number, y: number, delta: number, thickness: number) {
  const length = Math.max(1, Math.abs(delta));
  const r = Math.min(4, thickness / 2, length);
  const right = delta >= 0;
  const end = right ? baseline + length : baseline - length;
  return right
    ? `M${baseline},${y}H${end - r}Q${end},${y} ${end},${y + r}V${y + thickness - r}Q${end},${y + thickness} ${end - r},${y + thickness}H${baseline}Z`
    : `M${baseline},${y}H${end + r}Q${end},${y} ${end},${y + r}V${y + thickness - r}Q${end},${y + thickness} ${end + r},${y + thickness}H${baseline}Z`;
}

/** A bar with a 4 px rounded data end and a square baseline (ARTIFACTS-DESIGN §10.2). */
function barPath(x: number, baseline: number, width: number, delta: number) {
  const height = Math.max(1, Math.abs(delta));
  const r = Math.min(4, width / 2, height);
  const up = delta <= 0;
  const top = up ? baseline - height : baseline;
  return up
    ? `M${x},${baseline}V${top + r}Q${x},${top} ${x + r},${top}H${x + width - r}Q${x + width},${top} ${x + width},${top + r}V${baseline}Z`
    : `M${x},${top}H${x + width}V${top + height - r}Q${x + width},${top + height} ${x + width - r},${top + height}H${x + r}Q${x},${top + height} ${x},${top + height - r}Z`;
}
